<template>
  <div id="app" class="main-wrapper">
    <nav
      class="navbar navbar-expand-lg navbar-dark bg-primary fixed-top"
      id="sideNav"
    >
      <a class="navbar-brand js-scroll-trigger" href="#page-top">
        <span class="d-block d-lg-none">Menu</span>
        <span class="d-none d-lg-block">
          <img
            class="img-fluid img-profile rounded-circle mx-auto mb-2"
            :src="require_asset(profile_file)"
            alt=""
          />
        </span>
      </a>
      <button
        class="navbar-toggler"
        type="button"
        data-toggle="collapse"
        data-target="#navbarSupportedContent"
        aria-controls="navbarSupportedContent"
        aria-expanded="false"
        aria-label="Toggle navigation"
      >
        <span class="navbar-toggler-icon"></span>
      </button>
      <div class="collapse navbar-collapse" id="navbarSupportedContent">
        <ul class="navbar-nav">
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#about">About</a>
          </li>
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#experience"
              >Experience</a
            >
          </li>
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#education"
              >Education</a
            >
          </li>
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#skills">Skills</a>
          </li>
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#projects">Projects</a>
          </li>
          <li class="nav-item">
            <a class="nav-link js-scroll-trigger" href="#publications"
              >Publications</a
            >
          </li>
          <li class="nav-item">
            <a class="nav-link" href="http://blog.laserphile.com/">Blog</a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              href="https://github.com/d3v-null/portfolio-files"
              >Source Code</a
            >
          </li>
        </ul>
      </div>
    </nav>

    <div class="container-fluid p-0">
      <section class="resume-section p-3 p-lg-5 d-flex d-column" id="about">
        <div class="my-auto">
          <h1 class="mb-0">
            {{ name.first }} <span class="text-primary">{{ name.last }}</span>
          </h1>
          <div class="subheading mb-5">
            <a :href="phone_href">{{ contact.phone }}</a> ·
            <a :href="email_href">{{ contact.email }}</a>
          </div>
          <p class="mb-5">{{ about }}</p>
          <ul class="list-inline list-social-icons mb-0">
            <li class="list-inline-item">
              <a :href="github_href">
                <span class="fa-stack fa-lg">
                  <i class="fa fa-circle fa-stack-2x"></i>
                  <i class="fa fa-github fa-stack-1x fa-inverse"></i>
                </span>
              </a>
            </li>
            <li class="list-inline-item">
              <a :href="linkedin_href">
                <span class="fa-stack fa-lg">
                  <i class="fa fa-circle fa-stack-2x"></i>
                  <i class="fa fa-linkedin fa-stack-1x fa-inverse"></i>
                </span>
              </a>
            </li>
            <li class="list-inline-item">
              <a :href="twitter_href">
                <span class="fa-stack fa-lg">
                  <i class="fa fa-circle fa-stack-2x"></i>
                  <i class="fa fa-twitter fa-stack-1x fa-inverse"></i>
                </span>
              </a>
            </li>
            <li class="list-inline-item">
              <a :href="facebook_href">
                <span class="fa-stack fa-lg">
                  <i class="fa fa-circle fa-stack-2x"></i>
                  <i class="fa fa-facebook fa-stack-1x fa-inverse"></i>
                </span>
              </a>
            </li>
          </ul>
        </div>
      </section>

      <section
        class="resume-section p-3 p-lg-5 d-flex flex-column"
        id="experience"
      >
        <div class="my-auto">
          <h2 class="mb-5">Experience</h2>

          <div
            class="resume-item d-flex flex-column flex-md-row mb-5"
            v-for="job in experience"
            :key="job.company"
          >
            <div class="resume-content mr-auto">
              <h3 class="mb-0">{{ job.position }}</h3>
              <div class="subheading mb-3">{{ job.company }}</div>
              <p>{{ job.description }}</p>
              <ul>
                <li v-for="(item, index) in job.items" :key="index">
                  {{ item }}
                </li>
              </ul>
            </div>
            <div class="resume-date text-md-right">
              <span class="text-primary"
                >{{ job.startDate }} - {{ job.endDate }}</span
              >
            </div>
          </div>
        </div>
      </section>

      <section
        class="resume-section p-3 p-lg-5 d-flex flex-column"
        id="education"
      >
        <div class="my-auto">
          <h2 class="mb-5">Education</h2>

          <div
            class="resume-item d-flex flex-column flex-md-row mb-5"
            v-for="edu in education"
            :key="edu.qualification"
          >
            <div class="resume-content mr-auto">
              <h3 class="mb-0">{{ edu.institution }}</h3>
              <div class="subheading mb-3">{{ edu.qualification }}</div>
              <p>{{ edu.description }}</p>
              <ul>
                <li v-for="(point, index) in edu.points" :key="index">
                  {{ point }}
                </li>
              </ul>
            </div>
            <div class="resume-date text-md-right">
              <span class="text-primary">{{ edu.dates }}</span>
            </div>
          </div>
        </div>
      </section>

      <section class="resume-section p-3 p-lg-5 d-flex flex-column" id="skills">
        <div class="my-auto">
          <h2 class="mb-5">Skills</h2>

          <div class="subheading mb-3">Programming Languages</div>
          <ul class="list-inline list-icons">
            <li class="list-inline-item">
              <i class="devicons devicons-rust"></i>
            </li>
            <li class="list-inline-item">
              <i class="devicon-python-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-c-plain"></i>
            </li>
            <li class="list-inline-item">
              <i class="devicon-cplusplus-plain"></i>
            </li>
            <li class="list-inline-item">
              <i class="devicon-php-plain"></i>
            </li>
            <li class="list-inline-item">
              <i class="devicon-javascript-plain"></i>
            </li>
            <li class="list-inline-item">
              <i class="devicon-go-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-java-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicons devicons-haskell"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicons devicons-clojure"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-ruby-plain"></i>
              <!-- -wordmark -->
            </li>
            <!-- TODO: complete -->
          </ul>
          <div class="subheading mb-3">Web Technologies</div>
          <ul class="list-inline list-icons">
            <li class="list-inline-item">
              <i class="devicon-html5-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-css3-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-bootstrap-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-jquery-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-wordpress-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-nodejs-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-vuejs-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-apache-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-mysql-plain"></i>
              <!-- -wordmark -->
            </li>
          </ul>

          <div class="subheading mb-3">Tools & Workflow</div>
          <ul class="list-inline list-icons">
            <li class="list-inline-item">
              <i class="devicon-linux-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-git-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-docker-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicon-vim-plain"></i>
              <!-- -wordmark -->
            </li>
            <li class="list-inline-item">
              <i class="devicons devicons-google-cloud-platform"></i>
              <!-- -wordmark -->
            </li>
          </ul>
        </div>
      </section>

      <section
        class="resume-section p-3 p-lg-5 d-flex flex-column"
        id="projects"
      >
        <div class="my-auto">
          <h2 class="mb-5">Projects</h2>
          <div
            class="resume-item d-flex flex-column flex-md-row mb-5"
            v-for="project in projects"
            :key="project.title"
          >
            <div class="col-md-5">
              <img
                v-if="project.image"
                class="img-fluid rounded mb-3 mb-md-0"
                :src="require_asset(project.image)"
                width="320px"
                style="max-height: 240px; object-fit: contain"
              />
            </div>
            <div class="resume-content mr-auto col-md-7">
              <h3 class="mb-0">{{ project.title }}</h3>
              <div class="subheading mb-3">{{ project.role }}</div>
              <ul class="list-unstyled">
                <li
                  v-for="(badge, index) in project.badges"
                  :key="index"
                  v-html="badge.html"
                ></li>
              </ul>

              <p class="mb-0" v-html="project.description"></p>
              <ul>
                <li v-for="(link, index) in project.links" :key="index">
                  <a :href="link.url">{{ link.text }}</a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section
        class="resume-section p-3 p-lg-5 d-flex flex-column"
        id="publications"
      >
        <div class="my-auto">
          <h2 class="mb-5">Publications</h2>
          <div
            class="resume-item d-flex flex-column flex-md-row mb-5"
            v-for="paper in publications"
            :key="paper.url"
          >
            <div class="resume-content mr-auto">
              <h3 class="mb-0">
                <a class="text-reset" :href="paper.url">{{ paper.title }}</a>
              </h3>
              <div class="subheading mb-3">{{ paper.authors }}</div>
            </div>
            <div class="resume-date text-md-right">
              <span class="text-primary">{{ paper.year }}</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  data () {
    return {
      title: 'My Portfolio',
      name: {
        first: 'Dev',
        last: 'Null'
      },
      contact: {
        phone: '(+61) 416-160-912',
        email: 'dev.aleph.null@gmail.com'
      },
      about:
        'I build GPU-accelerated data processing pipelines that run on supercomputers, turning the firehose of data from radio telescopes like the Murchison Widefield Array and the SKA into science. I maintain open source Rust software used to preprocess and calibrate MWA data, I help build the SRCNet execution framework for the SKA Regional Centre Network, and I co-author papers setting upper limits on the 21 cm signal from the Epoch of Reionisation. Before astronomy I worked across web, IoT, augmented reality, machine learning, cloud infrastructure and information security, and I still love jumping into an unfamiliar stack to solve a hard engineering problem.',
      socials: {
        github: 'd3v-null',
        twitter: '_dev_0',
        facebook: 'derwentx',
        linkedin: 'derwentx'
      },
      experience: [
        {
          position: 'Senior Developer',
          company: 'Curtin Institute of Radio Astronomy',
          location: 'Bentley, WA',
          description: '',
          items: [
            'Maintain Birli and hyperdrive, the Rust preprocessing and GPU-accelerated (CUDA and HIP) calibration software for the Murchison Widefield Array, along with the hyperbeam, Marlu, mwalib and giant-squid libraries they are built on.',
            'Help build the execution framework for the SKA Regional Centre Network (SRCNet): a global computing broker and per-site job gateways that run science workflows on Kubernetes and Slurm across the network.',
            'Package and deploy the radio astronomy software stack on supercomputers with Spack, contributing to Pawsey and SKA SDP package repositories.',
            'Built Nextflow pipelines that let Epoch of Reionisation researchers run their analysis at an unprecedented scale, and contributed to the MWA EoR power spectrum limits and the SKA Science Data Challenge 3a.'
          ],
          startDate: 'Jan. 2021',
          endDate: 'Present'
        },
        {
          position: 'Developer Consultant',
          company: 'ThoughtWorks',
          location: 'Melbourne, VIC',
          description: '',
          items: [
            'Led a team of developers from around the world in building a VueJS web app for connecting victims of the Australian bushfire disaster with a searchable directory of helpful resources',
            'Helped the Personalization and Optimization team at an airline with the productionisation of their machine learning pipelines',
            "Set up multiple Azure DevOps pipelines which provisioned a large retail organization's cloud infrastructure using Terraform.",
            'Built an augmented reality app as a proof of concept for a major sporting organization.',
            'Helped a renewable energy company enrich the data visualizations in their React frontend and improve the test coverage of their GraphQL queries.',
            'Oversaw the security of multiple client engagements, facilitating threat modelling sessions and information security training.'
          ],
          startDate: 'Jan. 2019',
          endDate: 'Nov. 2020'
        },
        {
          position: 'Freelance IT Consultant',
          company: 'Laserphile',
          location: 'Melbourne, VIC',
          description: '',
          items: [
            'Built a completely bespoke eCommerce system for a client with a dynamic and challenging set of requirements',
            "Created a set of utilities which could synchronize user, product and order data between a client's CRM, Accounting and eCommerce databases",
            'Learnt first-hand, the importance of rigid version control and testing procedures in collaborative software development projects',
            'Applied leadership skills to organize teams spanning multiple disciplines and timezones',
            'Gained an awareness of significance of good information security practices when administrating web-facing applications',
            'Took on the role of a sysadmin in an environment that was under a constant barrage of DDoS and attempted defacement attacks where I learnt pre-emptive DDoS mitigation techniques.',
            'Performed incident response for a client whose web hosting company and backups had been taken down by an advanced attacker.'
          ],
          startDate: 'Jun. 2014',
          endDate: 'Jan. 2019'
        },
        {
          position: 'IT Infrastructure Manager',
          company: 'Anna Chandler Design',
          location: "O'Connor, WA",
          description: '',
          items: [
            'Provided IT support and POS system deployment to a fleet of 4 stores',
            'Designed, Built and Optimized a custom WooCommerce website which has vastly improved the business’ sales.',
            'Installed and Maintained a network of security camera systems across multiple locations',
            'Created a utility which automatically synchronizes product stock data between WooCommerce and Xero using their respective APIs',
            'Built a custom map generator around the Xero accounting API'
          ],
          startDate: 'Jan. 2010',
          endDate: 'Jun. 2014'
        }
      ],
      education: [
        {
          institution: 'University of Western Australia',
          qualification: 'B.Sc. in Computer Science and Physics',
          description: '',
          dates: '2010 - 2013',
          points: ['Grade Point Average: 4.931']
        }
      ],
      projects: [
        {
          title: 'hyperdrive',
          role: 'Calibration software for the MWA',
          description:
            'GPU-accelerated direction-independent calibration and sky-model subtraction for the Murchison Widefield Array, written in Rust with CUDA and HIP backends so it runs on both NVIDIA and AMD supercomputers.',
          image: 'project-hyperdrive.png',
          links: [
            {
              text: 'hyperdrive on GitHub',
              url: 'https://github.com/MWATelescope/mwa_hyperdrive'
            },
            {
              text: 'hyperbeam (MWA beam model) on GitHub',
              url: 'https://github.com/MWATelescope/mwa_hyperbeam'
            },
            {
              text: 'Marlu (shared Rust utilities) on GitHub',
              url: 'https://github.com/MWATelescope/Marlu'
            },
            {
              text: 'Conference paper (AP-RASC 2025)',
              url: 'https://doi.org/10.46620/ursiaprasc25/lscn1310'
            }
          ]
        },
        {
          title: 'Birli',
          role: 'Lead developer',
          description:
            'A Rust preprocessor for raw MWA correlator data: RFI flagging with AOFlagger, cable, geometric and Van Vleck corrections, averaging, and output to uvfits and Measurement Sets.',
          image: 'project-birli.png',
          links: [
            {
              text: 'Birli on GitHub',
              url: 'https://github.com/MWATelescope/Birli'
            }
          ]
        },
        {
          title: 'SRCNet Execution Framework',
          role: 'Computing broker and site job gateways',
          description:
            'The execution framework for the SKA Regional Centre Network: a global computing broker accepts science workflows and dispatches them to job gateways at each regional centre, which run them on Kubernetes or Slurm next to the data.',
          links: [
            {
              text: 'Computing broker on GitLab',
              url: 'https://gitlab.com/ska-telescope/src/src-ef/ska-src-ef-computing-broker'
            },
            {
              text: 'Local job gateway on GitLab',
              url: 'https://gitlab.com/ska-telescope/src/src-ef/ska-src-ef-local-job-gateway'
            },
            {
              text: 'SRC scientific workloads on GitLab',
              url: 'https://gitlab.com/ska-telescope/src/src-workloads'
            }
          ]
        },
        {
          title: 'TeleCortex',
          role: 'Protocol, firmware and controller software',
          description:
            'Helped fabricate custom controller boards, designed and optimised transmission protocols to maximise throughput, wrote and reverse engineered microcontroller firmware, and built highly performant server and client applications for synchronously transmitting pixel data to multiple microcontrollers as part of an interactive geodesic LED dome art project in collaboration with <a href="https://github.com/laserphile/">laserphile</a>.',
          image: 'project-telecortex-inside-dome.gif',
          links: [
            {
              text: 'Blog posts',
              url: 'http://blog.laserphile.com/search/label/Cortex'
            },
            {
              text: 'Arduino Firmware on GitHub',
              url: 'https://github.com/Laserphile/TeleCortex'
            },
            {
              text: 'Python controller on GitHub',
              url: 'https://github.com/Laserphile/Python-TeleCortex'
            },
            {
              text: 'NodeJS Rewrite of Server on GitHub',
              url: 'https://github.com/Laserphile/JS-Telecortex-2-Server'
            }
          ]
        },
        {
          title: 'PixelBlaze Expander NodeJS Library',
          description:
            'A NodeJS Library for controlling <a href="https://github.com/simap/pixelblaze_output_expander">PixelBlaze Expander</a> boards via serial port.',
          image: 'pbx_board.jpeg',
          links: [
            {
              text: 'NodeJS library on GitHub',
              url: 'https://github.com/d3v-null/js-pixelblaze-expander'
            }
          ]
        },
        {
          title: 'LEDPortal',
          description:
            'An interactive art installation with OpenGL driven animations parameters controlled by a leap motion',
          image: 'ledportal-leapmotion.gif',
          links: [
            {
              text: 'Java project on GitHub',
              url: 'https://github.com/flavius-space/ledportal-ide'
            }
          ]
        },
        {
          title: 'Moonbuggy',
          description:
            'Programmed the Arduino firmware for an electric vehicle art project in collaboration with Matt Callaghan',
          image: 'project-moonbuggy.png',
          links: [
            {
              text: 'Project on GitHub',
              url: 'https://github.com/d3v-null/Moonbuggy-Controller'
            }
          ]
        },
        {
          title: 'Modified 3D Printer',
          description:
            'Designed custom components and firmware for a specialised 3d printer that could simultaneously print multiple materials in collaboration with Matt Callaghan',
          image: 'project-3d-printer.jpg',
          links: [
            {
              text: 'Blog Posts',
              url: 'http://blog.laserphile.com/search/label/3d%20printing'
            },
            {
              text: 'Firmware on GitHub',
              url: 'https://github.com/d3v-null/Marlin-Laserphile'
            },
            {
              text: 'Open SCAD files on GitHub',
              url: 'https://github.com/d3v-null/hydra-bits'
            }
          ]
        }
      ],
      publications: [
        {
          title: 'The Murchison Widefield Array Phase III upgrade: Sensitivity Doubled, Number of Baselines Quadrupled, Flexibility Enhanced, and EoR Observations Optimised',
          authors: 'Tingay et al. (35 authors)',
          year: '2026',
          url: 'https://arxiv.org/abs/2606.01644'
        },
        {
          title: 'MWA_HYPERDRIVE: Next generation calibration software for the Murchison Widefield Array radio telescope',
          authors: 'Jordan, Null, et al. (AP-RASC 2025)',
          year: '2025',
          url: 'https://doi.org/10.46620/ursiaprasc25/lscn1310'
        },
        {
          title: '21cm Epoch of Reionisation studies with the Murchison Widefield Array: latest results and planning for SKA-Low using an SKA precursor',
          authors: 'Trott, Nunhokee, Null, Barry (AP-RASC 2025)',
          year: '2025',
          url: 'https://doi.org/10.46620/ursiaprasc25/xfye5150'
        },
        {
          title: 'Improved limits on the 21cm signal at z=6.5-7.0 with the MWA using Gaussian information',
          authors: 'Trott, Nunhokee, Null, et al.',
          year: '2025',
          url: 'https://arxiv.org/abs/2508.04164'
        },
        {
          title: 'Limits on the 21 cm power spectrum at z=6.5-7.0 from MWA observations',
          authors: 'Nunhokee, Null, et al.',
          year: '2025',
          url: 'https://arxiv.org/abs/2505.09097'
        },
        {
          title: 'Square Kilometre Array Science Data Challenge 3a: foreground removal for an EoR experiment',
          authors: 'Bonaldi et al. (184 authors)',
          year: '2025',
          url: 'https://arxiv.org/abs/2503.11740'
        },
        {
          title: 'Verifying the Australian MWA EoR pipeline II: fundamental limits of the AusEoRPipe and the impact of instrumental effects',
          authors: 'Line, Trott, Barry, Null, Jordan',
          year: '2025',
          url: 'https://arxiv.org/abs/2501.07004'
        }
      ],
      profile_file: 'profile.jpg'
    }
  },
  mounted () {
    const $ = this.jquery
    this.$nextTick(() => {
      'use strict' // Start of use strict

      // Smooth scrolling using jQuery easing
      $('a.js-scroll-trigger[href*="#"]:not([href="#"])').click(function () {
        if (
          location.pathname.replace(/^\//, '') ===
            this.pathname.replace(/^\//, '') &&
          location.hostname === this.hostname
        ) {
          var target = $(this.hash)
          target = target.length
            ? target
            : $('[name=' + this.hash.slice(1) + ']')
          if (target.length) {
            $('html, body').animate(
              {
                scrollTop: target.offset().top
              },
              1000,
              'easeInOutExpo'
            )
            return false
          }
        }
      })

      // Closes responsive menu when a scroll trigger link is clicked
      $('.js-scroll-trigger').click(function () {
        $('.navbar-collapse').collapse('hide')
      })

      // Activate scrollspy to add active class to navbar items on scroll
      $('body').scrollspy({
        target: '#sideNav'
      })

      this.$ua.trackView({ routeName: '/' })
    }) // End of use strict
  },
  methods: {
    require_asset: function (file) {
      if (file) {
        return require('./assets/' + file)
      }
    }
  },
  computed: {
    phone_href: function () {
      return 'tel:' + this.contact.phone.replace(/[ ()-]/g, '')
    },
    email_href: function () {
      return 'mailto:' + this.contact.email
    },
    github_href: function () {
      return 'https://github.com/' + this.socials.github
    },
    linkedin_href: function () {
      return 'https://www.linkedin.com/in/' + this.socials.linkedin
    },
    twitter_href: function () {
      return 'https://twitter.com/@' + this.socials.twitter
    },
    facebook_href: function () {
      return 'https://www.facebook.com/' + this.socials.facebook
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/App.vue